import * as smsService from './smsService';
import axios from 'axios';
import { normalizeNominationStatus } from '../../utils/evaluationStatusUtils';

/**
 * Service for handling all types of notifications including SMS
 */

/**
 * Send an SMS notification to a candidate
 * @param {Object} candidate - Candidate object with contact information
 * @param {string} message - Message content
 * @returns {Promise<Object>} - Response from the API
 */
export const sendCandidateSMS = async (candidate, message) => {
  try {
    // Extract phone number from candidate object
    // Handle different data structures
    const phoneNumber = candidate.personal_info?.phone || 
                        candidate.phone || 
                        candidate.personal_info?.mobile || 
                        candidate.mobile || 
                        '';
    
    if (!phoneNumber) {
      throw new Error('No phone number available for this candidate');
    }
    
    // Format phone number if needed
    const formattedPhone = phoneNumber.startsWith('+') ? 
      phoneNumber : 
      smsService.formatSaudiPhoneNumber ? 
        smsService.formatSaudiPhoneNumber(phoneNumber) : 
        phoneNumber;
    
    // Ensure message is properly encoded for Arabic text
    // Force is_unicode=true for Arabic messages
    console.log('Sending SMS with message:', message);
    return await smsService.sendSMS(formattedPhone, message, true);
  } catch (error) {
    console.error('Error sending candidate notification:', error);
    throw error;
  }
};

/**
 * Send a status update notification to a candidate
 * @param {Object} candidate - Candidate object with contact information
 * @param {string} status - New status of the candidate
 * @returns {Promise<Object>} - Response from the API
 */
export const sendStatusUpdateSMS = async (candidate, status) => {
  // Get candidate name
  const firstName = candidate.personal_info?.first_name || candidate.first_name || '';
  const lastName = candidate.personal_info?.last_name || candidate.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim();
  
  // Normalize legacy values and compose message based on unified statuses
  const normalized = normalizeNominationStatus(status);
  let message = '';
  switch (normalized) {
    case 'accepted':
      message = `مرحباً ${fullName}، نود إعلامكم بأنه تم قبول طلبكم في مجالسنا. نتطلع للقائكم قريباً.`;
      break;
    case 'rejected':
      message = `مرحباً ${fullName}، نشكركم على تقديمكم للانضمام إلى مجالسنا. نأسف لإبلاغكم بأنه لم يتم قبول طلبكم في هذه المرحلة.`;
      break;
    case 'shortlisted':
      message = `مرحباً ${fullName}، نود إعلامكم بأنه تم اختياركم مبدئياً للانضمام إلى مجالسنا. سنتواصل معكم قريباً بخصوص الخطوات التالية.`;
      break;
    case 'submitted':
      // Legacy 'under_review' is normalized to 'submitted'
      message = `مرحباً ${fullName}، طلبكم قيد المراجعة حالياً من قبل اللجنة. سنعلمكم بأي تحديثات قريباً.`;
      break;
    case 'pending':
      message = `مرحباً ${fullName}، تم الانتهاء من مراجعة طلبكم وهو الآن في انتظار النتيجة النهائية. سنوافيكم بالتفاصيل قريباً.`;
      break;
    case 'returned':
      message = `مرحباً ${fullName}، تمت إعادة طلبكم لإجراء بعض التعديلات. يرجى مراجعة حسابكم واستكمال المطلوب ثم إعادة الإرسال.`;
      break;
    case 'draft':
      message = `مرحباً ${fullName}، ما زال طلبكم في حالة مسودة. يرجى استكمال البيانات وإرسال الطلب لإتمام الإجراء.`;
      break;
    default:
      message = `مرحباً ${fullName}، نود إعلامكم بأنه تم تحديث حالة طلبكم في مجالسنا. يرجى مراجعة حسابكم للمزيد من التفاصيل.`;
  }
  
  // Send the notification
  return await sendCandidateSMS(candidate, message);
};

/**
 * Send a bulk SMS notification to multiple candidates
 * @param {Array<Object>} candidates - Array of candidate objects
 * @param {string} message - Message content
 * @returns {Promise<Array<Object>>} - Array of responses from the API
 */
export const sendBulkSMS = async (candidates, message) => {
  const promises = candidates.map(candidate => sendCandidateSMS(candidate, message));
  return Promise.all(promises);
};

const notificationService = {
  sendCandidateSMS,
  sendStatusUpdateSMS,
  sendBulkSMS
};

export default notificationService;
